import { format } from "date-fns";
import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  useAccountByDateQuery,
  useUpdateAccountDeleteMutation,
} from "../../services/accountApi";
import SelectAccountHead from "../Common/CustomSelect/SelectAccountHead";
import Header from "../Common/Header/Header";
import SideBar from "../Common/SideBar/SideBar";
import { signInUser } from "../Utility/Auth";
import { notify } from "../Utility/Notify";
import AccountView from "../Accounts/AccountView";

const Ledger = () => {
  const auth = signInUser();
  const { aamarId } = auth || {};
  const lang = useSelector((state) => state.languageReducer);

  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [accountHead, setAccountHead] = useState("");
  const [accountId, setAccountId] = useState("");
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const { data, refetch } = useAccountByDateQuery(
    {
      startDate,
      endDate,
      aamarId,
    },
    { skip: !aamarId }
  );
  const [deleteUpdateAccount] = useUpdateAccountDeleteMutation();

  useEffect(() => {
    if (aamarId && refetch) {
      refetch();
    }
  }, [startDate, endDate, aamarId]);

  const handleAcHeadChange = (e) => {
    if (e && e.option && e.option !== "no-accountHeads") {
      setAccountHead(e.option);
    } else {
      setAccountHead("");
    }
  };

  const handleAccountsView = (id) => {
    setAccountId(id);
    setShow(true);
  };

  const deleteHandler = async (id) => {
    const result = await Swal.fire({
      title: "Are you Sure?",
      text: "Delete this Transaction?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      await deleteUpdateAccount({ _id: id })
        .then(() => {
          notify("Account Deleted Successfully", "success");
          if (refetch) refetch();
        })
        .catch((err) => {
          console.error(err);
          notify("Can not delete account", "error");
        });
    }
  };

  const filteredData = data?.filter((ac) => {
    if (!accountHead || accountHead === "no-accountHeads") return true;
    const headId = ac?.accountHead?._id || ac?.accountHead;
    return headId === accountHead;
  });

  const totalAmount = filteredData?.reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0
  ) || 0;

  let i = 1;

  return (
    <div>
      <div className="container-fluid ">
        <div className="row">
          <div className="col-md-2">
            <SideBar></SideBar>
          </div>
          <div className="col-md-10">
            <Header title={lang?.ledgers}></Header>
            <div className="row mt-2">
              <div className="col-md-12">
                <div className="row align-items-center">
                  <div className="col-md-6 mt-2 mb-2">
                    <div className="date-picker d-flex gap-1 align-items-center">
                      <b>Start:</b>
                      <DatePicker
                        selected={new Date(startDate)}
                        className="form-control me-2"
                        onChange={(date) =>
                          setStartDate(format(new Date(date), "MM-dd-yyyy"))
                        }
                      />
                      <span width="10px"></span>
                      <b>End:</b>
                      <DatePicker
                        selected={new Date(endDate)}
                        className="form-control"
                        onChange={(date) =>
                          setEndDate(format(new Date(date), "MM-dd-yyyy"))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-4 mt-2 mb-2">
                    <SelectAccountHead
                      handleOnchange={handleAcHeadChange}
                    ></SelectAccountHead>
                  </div>
                  <div className="col-md-2 mt-2 mb-2 text-end">
                    <span className="badge bg-dark p-2 fs-6">
                      Total: {totalAmount.toFixed(2)} TK
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-2">
              <div className="table-responsive">
                <table className="table table-striped ">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">AC Code</th>
                      <th scope="col">Account Head</th>
                      <th scope="col">Date</th>
                      <th scope="col">Method</th>
                      <th scope="col">Receiver / Vendor</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Actions </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData?.length > 0 ? (
                      filteredData?.map((ac) => (
                        <tr key={ac?._id}>
                          <th scope="row">{i++}</th>
                          <td>{ac?.acId}</td>
                          <td>{ac?.accountHead?.name || "General"}</td>
                          <td>
                            {ac?.createdAt
                              ? format(
                                  new Date(ac.createdAt),
                                  "yyyy-MM-dd HH:mm"
                                )
                              : ac?.date}
                          </td>
                          <td>
                            {ac?.paymentMethodType || "Cash"}{" "}
                            <small className="text-muted">({ac?.type || "Debit"})</small>
                          </td>
                          <td>
                            {ac?.paidTo ||
                              ac?.supplier?.company ||
                              ac?.supplier?.name ||
                              "-"}
                          </td>
                          <td>
                            <b>{Number(ac?.amount || 0).toFixed(2)} TK</b>
                          </td>
                          <td>
                            <Icons.EyeOutline
                              onClick={() => handleAccountsView(ac._id)}
                              className="icon-eye me-2"
                              style={{ cursor: "pointer" }}
                              size={20}
                            ></Icons.EyeOutline>
                            <Icons.TrashOutline
                              className="icon-trash"
                              size={20}
                              style={{ cursor: "pointer" }}
                              onClick={() => deleteHandler(ac._id)}
                            ></Icons.TrashOutline>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          No Ledger Transactions Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {filteredData?.length > 0 && (
                    <tfoot>
                      <tr>
                        <th colSpan={6} className="text-end">
                          Total Amount:
                        </th>
                        <th colSpan={2}>
                          {totalAmount.toFixed(2)} TK
                        </th>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AccountView
        show={show}
        handleClose={handleClose}
        accountId={accountId}
      ></AccountView>
    </div>
  );
};

export default Ledger;
